/**
 * MSA Invoicing Store
 *
 * Lightweight store backing the nav badge for the MSA Invoicing report. It
 * keeps a live count of payments that need an invoice created (the report's
 * "Action Needed" total) via a real-time listener on MSA-order projects.
 *
 * Kept separate from the report view so the badge works regardless of whether
 * the report is open.
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import { db } from '@/js/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { logger } from '@/services/loggerService';

const ACTIVE_STATUSES = ['Active', 'In Review'];
const SKIP_STATUSES = ['Canceled', 'Draft', 'archived'];
const NEEDS_INVOICE = 'needs_invoice';
const PAID = 'paid';

/**
 * Count the payments awaiting an invoice across all in-scope MSA projects.
 * @param {Array} docs - Firestore query docs
 * @returns {number}
 */
function countNeedsInvoice(docs) {
  let count = 0;

  for (const docSnap of docs) {
    const p = docSnap.data();
    if (p.type !== 'proposal' || !p.quote || typeof p.quote.total !== 'number') continue;
    if (SKIP_STATUSES.includes(p.status)) continue;

    const inv = p.invoicing || {};
    const hasUnpaid =
      (inv.deposit && inv.deposit.status !== PAID) ||
      (inv.final?.enabled && inv.final.status !== PAID);
    const inScope = ACTIVE_STATUSES.includes(p.status) || hasUnpaid;
    if (!inScope) continue;

    if (inv.deposit?.status === NEEDS_INVOICE) {
      count += 1;
    } else if (!inv.deposit && ACTIVE_STATUSES.includes(p.status)) {
      // Active/In-Review project not yet initialized → deposit will need invoicing
      count += 1;
    }
    if (inv.final?.enabled && inv.final.status === NEEDS_INVOICE) {
      count += 1;
    }
  }

  return count;
}

export const useMsaInvoicingStore = defineStore('msaInvoicing', () => {
  const actionCount = ref(0);
  const initialized = ref(false);
  let unsubscribe = null;

  function init() {
    if (initialized.value) return;
    initialized.value = true;

    try {
      const q = query(collection(db, 'projects'), where('type', '==', 'proposal'));
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          actionCount.value = countNeedsInvoice(snapshot.docs);
        },
        (error) => {
          logger.firestoreListenerError('[MsaInvoicingStore] Snapshot error', error);
        }
      );
    } catch (error) {
      logger.error('[MsaInvoicingStore] Failed to init', error);
    }
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    initialized.value = false;
    actionCount.value = 0;
  }

  return { actionCount, initialized, init, teardown };
});
